import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Globe2 } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import { useAnalytics, useAttribution, useSaveSite, useSite } from './api';

/**
 * The website side (HubSpot's tracking code and traffic analytics): the
 * snippet, visitors and page views, where they came from, and which
 * campaigns turned into leads and revenue.
 */
export function WebsitePage() {
  const { can } = usePermission();
  const [days, setDays] = useState(30);
  const { data: site } = useSite();
  const { data: stats } = useAnalytics(days);
  const { data: attribution } = useAttribution(days);
  const save = useSaveSite();
  const [domains, setDomains] = useState('');

  useEffect(() => {
    if (site) setDomains(site.domains.join(', '));
  }, [site]);

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value).catch(() => undefined);
    toast.success('Copied');
  };

  const saveDomains = () => {
    if (!site) return;
    save.mutate(
      {
        domains: domains.split(/[\s,]+/).map((item) => item.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '')).filter(Boolean),
        helpCenterSlug: site.helpCenterSlug,
        helpCenterName: site.helpCenterName,
        helpCenterColour: site.helpCenterColour,
        helpCenterEnabled: site.helpCenterEnabled,
      },
      { onSuccess: () => toast.success('Saved'), onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not save') },
    );
  };

  return (
    <div>
      <PageHeader
        title="Website & tracking"
        description="Add one line to your website to count visitors, show your pop-up forms, and see the pages every lead read before they wrote to you."
        breadcrumbs={[{ label: 'Marketing hub' }, { label: 'Website & tracking' }]}
        actions={
          <Select className="w-40" value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {[7, 30, 90, 365].map((value) => (
              <option key={value} value={value}>
                Last {value} days
              </option>
            ))}
          </Select>
        }
      />

      {site && (
        <Card className="mb-5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe2 className="h-4 w-4" /> Your tracking code
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Paste it before &lt;/head&gt; on every page — or in WordPress, Wix or Webflow’s “custom code” setting.</p>
            <div className="flex gap-2">
              <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2.5 text-xs text-slate-100">{site.snippet}</code>
              <Button variant="outline" onClick={() => copy(site.snippet)}>
                <Copy /> Copy
              </Button>
            </div>
            {can('marketing.site.update') && (
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[16rem] flex-1 space-y-1">
                  <Label className="text-xs">Only allow these domains (optional)</Label>
                  <Input value={domains} onChange={(event) => setDomains(event.target.value)} placeholder="acme.ae, shop.acme.ae" />
                </div>
                <Button variant="outline" onClick={saveDomains} disabled={save.isPending}>
                  Save
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {stats && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
            {(
              [
                ['Visitors', stats.visitors],
                ['Page views', stats.views],
                ['Form submissions', stats.submissions],
                ['Visitor → lead', `${stats.conversionRate}%`],
              ] as const
            ).map(([label, value]) => (
              <Card key={label}>
                <CardContent className="pt-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                  <p className="text-2xl font-extrabold">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mb-5">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Traffic</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              {stats.daily.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily}>
                    <defs>
                      <linearGradient id="views" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(228 86% 54%)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(228 86% 54%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="views" name="Page views" stroke="hsl(228 86% 54%)" fill="url(#views)" strokeWidth={2} />
                    <Area type="monotone" dataKey="visitors" name="Visitors" stroke="hsl(196 94% 48%)" fill="transparent" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">No visits yet — add the tracking code to your site.</p>
              )}
            </CardContent>
          </Card>

          <div className="mb-5 grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Where visitors come from</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-end">Visitors</TableHead>
                      <TableHead className="text-end">Became leads</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.sources.map((row) => (
                      <TableRow key={`${row.source}-${row.medium}`}>
                        <TableCell>
                          {row.source}
                          {row.medium && <span className="text-xs text-muted-foreground"> / {row.medium}</span>}
                        </TableCell>
                        <TableCell className="text-end">{row.visitors}</TableCell>
                        <TableCell className="text-end">{row.leads}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top pages</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Page</TableHead>
                      <TableHead className="text-end">Views</TableHead>
                      <TableHead className="text-end">Visitors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.pages.map((row) => (
                      <TableRow key={row.path}>
                        <TableCell className="max-w-[16rem] truncate font-mono text-xs">{row.path}</TableCell>
                        <TableCell className="text-end">{row.views}</TableCell>
                        <TableCell className="text-end">{row.visitors}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {attribution && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Campaign attribution</CardTitle>
            <p className="text-sm text-muted-foreground">Leads created in the period by source and campaign, and what their deals won.</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {attribution.rows.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No leads in this period.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-end">Leads</TableHead>
                    <TableHead className="text-end">Converted</TableHead>
                    <TableHead className="text-end">Deals won</TableHead>
                    <TableHead className="text-end">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attribution.rows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.source}</TableCell>
                      <TableCell className="text-muted-foreground">{row.campaign ?? '—'}</TableCell>
                      <TableCell className="text-end">{row.leads}</TableCell>
                      <TableCell className="text-end">{row.converted}</TableCell>
                      <TableCell className="text-end">{row.won}</TableCell>
                      <TableCell className="text-end font-semibold">{formatMoney(row.revenue, attribution.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
